import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Check, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  "h-10 w-full border border-neutral-800 bg-black/40 px-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none";

function MemberCard({ member, headers, onChange, onUnauthorized }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: member.name, role: member.role, badge: member.badge || "" });
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const guard = (err) => {
    if (err.response?.status === 401) onUnauthorized();
    else toast(err.response?.data?.detail || "Operazione non riuscita");
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.role.trim().length < 2) {
      toast("Nome e ruolo devono avere almeno 2 caratteri");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/admin/members/${member.id}`, form, { headers: headers() });
      onChange(data);
      setEditing(false);
      toast("Scheda aggiornata", { description: data.name });
    } catch (err) {
      guard(err);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("Seleziona un file immagine");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await axios.post(`${API}/api/admin/members/${member.id}/photo`, fd, {
        headers: headers(),
        onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50),
      });
      onChange(data);
      toast("Foto aggiornata", { description: data.name });
    } catch (err) {
      guard(err);
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <article data-testid={`admin-member-${member.tag}`} className="border border-neutral-800 bg-[#0D0D0D]">
      <div className="group relative aspect-[4/3] overflow-hidden">
        <img src={resolveSrc(member.photo)} alt={member.name} className="h-full w-full object-cover object-[50%_28%]" />
        <button
          type="button"
          data-testid={`admin-member-${member.tag}-photo-button`}
          onClick={() => fileRef.current?.click()}
          disabled={progress !== null}
          className="absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-all duration-200 hover:bg-black/60 hover:text-white focus:bg-black/60 focus:text-white"
        >
          <span className="inline-flex items-center gap-2 border border-white/40 px-3 py-2 font-mono-tac text-[10px] uppercase tracking-[0.25em]">
            {progress !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            {progress !== null ? `${progress}%` : "Cambia foto"}
          </span>
        </button>
        <input ref={fileRef} data-testid={`admin-member-${member.tag}-file-input`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
      </div>

      {editing ? (
        <form onSubmit={save} className="space-y-2 p-4">
          <input data-testid={`admin-member-${member.tag}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className={inputCls} />
          <input data-testid={`admin-member-${member.tag}-role-input`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Ruolo (es. Basso)" className={inputCls} />
          <input data-testid={`admin-member-${member.tag}-badge-input`} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Badge (es. endorsement) — opzionale" className={inputCls} />
          <div className="flex gap-2 pt-1">
            <button type="submit" data-testid={`admin-member-${member.tag}-save`} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#C8102E] px-3 py-2 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B] disabled:opacity-60">
              <Check className="h-3.5 w-3.5" /> Salva
            </button>
            <button type="button" onClick={() => { setEditing(false); setForm({ name: member.name, role: member.role, badge: member.badge || "" }); }} className="inline-flex items-center gap-1.5 border border-neutral-800 px-3 py-2 font-mono-tac text-[10px] uppercase tracking-[0.2em] text-neutral-400 hover:text-red-500">
              <X className="h-3.5 w-3.5" /> Annulla
            </button>
          </div>
        </form>
      ) : (
        <div className="flex items-start justify-between gap-3 p-4">
          <div className="min-w-0">
            <p className="font-serif-logo truncate text-sm font-bold tracking-wide text-neutral-100">{member.name}</p>
            <p className="font-mono-tac mt-1 text-[10px] uppercase tracking-[0.25em] text-red-600">{member.role}</p>
            {member.badge && <p className="mt-1 truncate text-[11px] text-neutral-500">{member.badge}</p>}
          </div>
          <button data-testid={`admin-member-${member.tag}-edit`} onClick={() => setEditing(true)} aria-label={`Modifica ${member.name}`} className="flex h-8 w-8 shrink-0 items-center justify-center border border-neutral-800 text-neutral-400 hover:border-red-700 hover:text-red-500">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </article>
  );
}

export default function MembersAdmin({ headers, onUnauthorized }) {
  const [members, setMembers] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/members`);
      setMembers(data);
    } catch {
      toast("Impossibile caricare la band");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <p className="text-sm text-neutral-400">
        Clicca sulla foto per cambiarla (viene ritagliata al centro-alto nelle card del sito), sulla matita per nome, ruolo e badge.
      </p>
      <div data-testid="admin-members-grid" className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {members?.map((m) => (
          <MemberCard key={m.id} member={m} headers={headers} onUnauthorized={onUnauthorized} onChange={(upd) => setMembers((list) => list.map((x) => (x.id === upd.id ? upd : x)))} />
        ))}
      </div>
    </div>
  );
}
